import { useAppDispatch } from '@/hooks';
import { login } from '@/store';
import { useVerifyNumberMutation, useVerifyTelegramMutation } from '@/store/api/mfaApi';
import { showToast } from '@/utils';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

type OTPModalProps = {
  open: boolean;
  onClose: () => void;
  data: { username: string; type: string };
};

const OTPModal = ({ open, onClose, data }: OTPModalProps) => {
  const [verifyNumber, { isLoading: numberLoading }] = useVerifyNumberMutation();
  const [verifyTelegram, { isLoading: telegramLoading }] = useVerifyTelegramMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [code, setCode] = useState(Array(6).fill(''));
  const inputs = useRef<HTMLInputElement[]>([]);

  if (!open) return null;

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otp = code.join('');

    if (otp.length !== 6) {
      showToast('Please enter complete OTP', 'error');
      return;
    }

    try {
      let result;

      if (data.type === 'telegram') {
        result = await verifyTelegram({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
        navigate('/dashboard');
      } else {
        result = await verifyNumber({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
      }
      navigate('/home');

      onClose();
    } catch (error: any) {
      showToast(error?.data?.message || 'Verification failed', 'error');
    }
  };

  const loading = numberLoading || telegramLoading;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-secondary/40 z-50">
      <div className="bg-surface rounded-md shadow-dropdown w-[420px] p-6 text-center">
        <h2 className="text-info-blue-muted font-semibold text-lg">Security Code Verification</h2>
        <div className="flex justify-center gap-3 mt-6">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                if (el) inputs.current[index] = el;
              }}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleBackspace(e, index)}
              className="w-12 h-12 border border-gray-300 rounded text-center text-lg focus:outline-none focus:border-primary"
            />
          ))}
        </div>

        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={() => {
              setCode(Array(6).fill(''));
              onClose();
            }}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/80 disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPModal;
